import React, { useRef, useState } from "react";
import useStateRef from "react-usestateref";
import type {
  TextFieldProps} from "@mui/material";
import {
  Backdrop,
  ClickAwayListener,
  FormControl,
  IconButton,
  Paper,
  Popper,
} from "@mui/material";
import type { Option } from "@fas/cpa-state-manager/redux/reducers";
import { withFormField } from "@fas/ui-core";
import { ArrowDropDown, ArrowDropUp, Close } from "@mui/icons-material";
import { mergeClasses } from "@fas/cpa-cabinet-ui/lib/utils";
import { makeStyles } from "@mui/styles";
import { TextFieldForm } from "../TextFieldForm/TextFieldForm";
import SelectBody, { toOptionValue } from "./SelectBody";

export type Props<T> = {
  value?: T;
  onChange?: (arg0: T) => any;
  slotProps?: any;
  defaultValue: T;
  options: Option[];
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  disableBackdrop?: boolean;
  disableCloseOnSelect?: boolean;
  isSplitListBoxColumn?: boolean;
  disableSearch?: boolean;
  disableClearable?: boolean;
  name: string;
  label?: string;
  modalLabel?: string;
  classes?: any;
  inputProps?: any;
  formControlSx?: any;
  StartIcon?: React.JSX.Element;
  EndIcon?: React.JSX.Element;
  getOptionDisabled?: (arg0: any) => boolean;
  maxSelection?: number;
  onKeyPress?: TextFieldProps["onKeyPress"];
};
const sx = {
  fontSize: "inherit",
  color: "inherit",
  "&&&": {
    fill: "currentColor",
  },
};
const useStyles = makeStyles((theme) => ({
  header: {
    display: "none",
  },
  paperBody: {
    backgroundColor: theme.palette.background.list,
    padding: 5,
  },
  inputField: {
    "&:hover": {
      "& $adornmentButton": {
        visibility: "visible",
      },
    },
  },
  adornmentButton: {
    visibility: "hidden",
  },
  icon: {
    marginRight: "-4px",
    padding: 0,
  },
}));

function SelectPopup<T extends string | string[]>({
  defaultValue,
  disableCloseOnSelect,
  disableSearch,
  disableClearable,
  isSplitListBoxColumn,
  value,
  onChange,
  options = [],
  isLoading,
  name,
  label,
  disabled,
  classes: propClasses = {},
  disableBackdrop = false,
  formControlSx,
  getOptionDisabled,
  maxSelection,
  ...props
}: Props<T>) {
  const classes: Record<string, any> = mergeClasses(useStyles(), propClasses);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingValue, setPendingValue, pendingValueRef] = useStateRef<T>([] as unknown as T);
  const [error, setError] = useState<string>("");
  const ref = useRef<HTMLDivElement | null>(null);

  const handleOpen = () => {
    if (anchorEl || disabled) {
      return;
    }

    setPendingValue(value!);
    setAnchorEl(ref.current && (ref.current.firstChild as HTMLElement));
  };

  const handleClose = (e?: { preventDefault: () => unknown }) => {
    if (value !== pendingValueRef.current && onChange) {
      onChange(pendingValueRef.current);
    }

    setAnchorEl(null);
    setError("");
    e && e.preventDefault();
  };

  const inputValue: Option[] | Option = toOptionValue(value || "", options);
  const hasValue: boolean = Array.isArray(value) ? !!value.length : !!value;
  const textFieldValue: string = Array.isArray(inputValue)
    ? inputValue.map(({ title }) => title).join(", ")
    : inputValue.title;
  const isIdValue: boolean = typeof value === "number";
  const isLoadingOptionsForIdValue: boolean = isIdValue && options.length === 0;
  return (
    <>
      <FormControl ref={ref} fullWidth>
        <TextFieldForm
          {...props}
          disabled={disabled}
          formControlSx={formControlSx}
          name={name}
          label={label || ""}
          isAuthenticated
          value={isLoadingOptionsForIdValue ? "" : textFieldValue}
          className={classes.inputField}
          inputProps={{
            ...props.inputProps,
            readOnly: true,
            onClick: handleOpen,
          }}
          error={error}
          EndIcon={
            <>
              {hasValue && !disabled && !disableClearable ? (
                <IconButton
                  disabled={disabled}
                  data-testid="clear"
                  size="small"
                  onClick={() => onChange && onChange(defaultValue)}
                  className={classes.adornmentButton}
                >
                  <Close sx={sx} />
                </IconButton>
              ) : null}
              <IconButton
                disabled={disabled}
                data-testid="open"
                size="small"
                onClick={handleOpen}
                className={classes.icon}
              >
                {anchorEl ? <ArrowDropUp sx={sx} /> : <ArrowDropDown sx={sx} />}
              </IconButton>
              {props.EndIcon}
            </>
          }
        />
        <Backdrop
          invisible
          sx={{
            zIndex: "modal",
          }}
          open={!disableBackdrop && Boolean(anchorEl)}
          onClick={handleClose}
        />
      </FormControl>
      <Popper
        // disabled={disabled}
        open={Boolean(anchorEl)}
        sx={{
          zIndex: "modal",
          width: anchorEl && anchorEl.clientWidth,
        }}
        anchorEl={anchorEl}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper className={classes.paperBody}>
            <SelectBody
              label={label}
              value={pendingValue}
              onChange={(newValue) => {
                if (
                  !maxSelection ||
                  (Array.isArray(newValue) &&
                    maxSelection &&
                    newValue.length <= maxSelection)
                ) {
                  setPendingValue(newValue);
                  setError("");
                }

                if (maxSelection && newValue.length > maxSelection) {
                  setError(`You can select up to ${maxSelection} items.`);
                }
              }}
              onClose={handleClose}
              options={options}
              isLoading={isLoading}
              defaultValue={defaultValue}
              disableCloseOnSelect={disableCloseOnSelect}
              isSplitListBoxColumn={isSplitListBoxColumn}
              disableSearch={disableSearch}
              classes={classes}
              getOptionDisabled={getOptionDisabled}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

const SelectPopupField = withFormField(SelectPopup);
export { SelectPopupField };
export default SelectPopup;
